import React, { useState, useRef, useEffect } from 'react'
import { Spin } from 'antd'
import './InstagramVideo.css'

interface InstagramVideoProps {
    videoUrl: string
}

const InstagramVideo: React.FC<InstagramVideoProps> = ({ videoUrl }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [shouldLoad, setShouldLoad] = useState(false)
    const videoRef = useRef<HTMLVideoElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const currentContainer = containerRef.current
        if (!currentContainer || shouldLoad) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setShouldLoad(true)
                    }
                })
            },
            {
                rootMargin: '50px', // Start loading 50px before the video enters viewport
            }
        )

        observer.observe(currentContainer)

        return () => {
            observer.disconnect()
        }
    }, [shouldLoad])

    const handleVideoLoadStart = () => {
        setIsLoading(true)
    }

    const handleVideoCanPlay = () => {
        setIsLoading(false)
    }

    const handleVideoError = () => {
        setIsLoading(false)
    }

    return (
        <div className="image-group" ref={containerRef}>
            {isLoading && (
                <div className="video-loading-overlay">
                    <Spin size="large" />
                </div>
            )}
            {shouldLoad ? (
                <video
                    ref={videoRef}
                    className="instagram-video"
                    controls
                    playsInline
                    autoPlay
                    loop
                    muted
                    poster="/img/video-poster.jpg"
                    onLoadStart={handleVideoLoadStart}
                    onCanPlay={handleVideoCanPlay}
                    onError={handleVideoError}
                >
                    <source
                        src={videoUrl}
                        type="video/mp4"
                    />
                    Your browser does not support the video tag.
                </video>
            ) : (
                <div className="instagram-video video-placeholder">
                    <div className="video-placeholder-content">
                        <Spin size="large" />
                    </div>
                </div>
            )}
        </div>
    )
}

export default InstagramVideo
